import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  Heart, Users, Calendar, Clock, CheckCircle, AlertTriangle,
  RefreshCw, ChevronLeft, Phone, Mail, FileText, Star,
  UserPlus, ClipboardList, Activity, TrendingUp, Award, MessageCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BEREAVEMENT_RISK = {
  high: { label: "High Risk", color: "bg-red-500", textColor: "text-red-400" },
  moderate: { label: "Moderate", color: "bg-yellow-500", textColor: "text-yellow-400" },
  standard: { label: "Standard", color: "bg-green-500", textColor: "text-green-400" }
};

const GRIEF_STAGES = ["denial", "anger", "bargaining", "depression", "acceptance"];

export default function VolunteerBereavement() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("volunteers");
  const [loading, setLoading] = useState(true);
  const [volunteers, setVolunteers] = useState([]);
  const [bereavementCases, setBereavementCases] = useState([]);
  const [dueFollowUps, setDueFollowUps] = useState([]);
  const [stats, setStats] = useState({});
  const [fivePercentReport, setFivePercentReport] = useState(null);
  
  // Dialogs
  const [volunteerDialog, setVolunteerDialog] = useState(false);
  const [visitLogDialog, setVisitLogDialog] = useState(false);
  const [bereavementDialog, setBereavementDialog] = useState(false);
  const [assessmentDialog, setAssessmentDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Forms
  const [volunteerForm, setVolunteerForm] = useState({
    name: "", email: "", phone: "", skills: "", availability: ""
  });
  const [visitForm, setVisitForm] = useState({
    volunteer_id: "", chart_id: "", hours: 1, activities: "", notes: ""
  });
  const [assessmentForm, setAssessmentForm] = useState({
    grief_stage: "denial", coping_score: 5, support_level: "adequate", notes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("hospice_token");
      
      // Fetch dashboard summary
      const summaryRes = await fetch(`${API_URL}/api/ehr/volunteer-bereavement/dashboard/summary`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setStats(data);
        setVolunteers(data.volunteers || []);
        setBereavementCases(data.bereavement_cases || []);
      }

      // Fetch due follow-ups
      const followUpsRes = await fetch(`${API_URL}/api/ehr/volunteer-bereavement/bereavement/due-follow-ups`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (followUpsRes.ok) {
        const data = await followUpsRes.json();
        setDueFollowUps(data.due_follow_ups || []);
      }

      // Fetch 5% report
      const reportRes = await fetch(`${API_URL}/api/ehr/volunteer-bereavement/volunteers/5-percent-report`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (reportRes.ok) {
        const data = await reportRes.json();
        setFivePercentReport(data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const registerVolunteer = async () => {
    if (!volunteerForm.name || !volunteerForm.email) {
      toast.error("Name and email are required");
      return;
    }

    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(`${API_URL}/api/ehr/volunteer-bereavement/volunteers/register`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...volunteerForm,
          skills: volunteerForm.skills.split(",").map(s => s.trim()).filter(Boolean)
        })
      });

      if (response.ok) {
        toast.success("Volunteer registered successfully");
        setVolunteerDialog(false);
        setVolunteerForm({ name: "", email: "", phone: "", skills: "", availability: "" });
        fetchData();
      } else {
        toast.error("Failed to register volunteer");
      }
    } catch (error) {
      console.error("Error registering volunteer:", error);
      toast.error("Failed to register volunteer");
    }
  };

  const logVisit = async () => {
    if (!visitForm.volunteer_id || !visitForm.chart_id || !visitForm.hours) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(`${API_URL}/api/ehr/volunteer-bereavement/volunteers/visits`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...visitForm,
          activities: visitForm.activities.split(",").map(a => a.trim()).filter(Boolean),
          visit_date: new Date().toISOString()
        })
      });

      if (response.ok) {
        toast.success("Visit logged successfully");
        setVisitLogDialog(false);
        setVisitForm({ volunteer_id: "", chart_id: "", hours: 1, activities: "", notes: "" });
        fetchData();
      } else {
        toast.error("Failed to log visit");
      }
    } catch (error) {
      console.error("Error logging visit:", error);
      toast.error("Failed to log visit");
    }
  };

  const completeFollowUp = async (bereavementId, followUpId) => {
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(
        `${API_URL}/api/ehr/volunteer-bereavement/bereavement/${bereavementId}/complete-follow-up`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            follow_up_id: followUpId,
            completed_by: localStorage.getItem("userId") || "clinician",
            notes: "Follow-up completed"
          })
        }
      );

      if (response.ok) {
        toast.success("Follow-up completed");
        fetchData();
      } else {
        toast.error("Failed to complete follow-up");
      }
    } catch (error) {
      console.error("Error completing follow-up:", error);
      toast.error("Failed to complete follow-up");
    }
  };

  const submitAssessment = async () => {
    if (!selectedItem) return;

    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(`${API_URL}/api/ehr/volunteer-bereavement/bereavement/assessment`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          bereavement_id: selectedItem.id,
          ...assessmentForm,
          assessed_by: localStorage.getItem("userId") || "clinician"
        })
      });

      if (response.ok) {
        toast.success("Assessment submitted");
        setAssessmentDialog(false);
        setSelectedItem(null);
        setAssessmentForm({ grief_stage: "denial", coping_score: 5, support_level: "adequate", notes: "" });
        fetchData();
      } else {
        toast.error("Failed to submit assessment");
      }
    } catch (error) {
      console.error("Error submitting assessment:", error);
      toast.error("Failed to submit assessment");
    }
  };

  const volunteerSummary = stats.volunteer_summary || {};
  const bereavementSummary = stats.bereavement_summary || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-950 via-pink-900 to-purple-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-800/80 to-pink-800/80 backdrop-blur border-b border-rose-500/30">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                className="text-rose-200 hover:bg-rose-700/50"
                onClick={() => navigate("/hospice-ehr/command-center")}
                data-testid="back-btn"
              >
                <ChevronLeft className="w-5 h-5 mr-1" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                  <Heart className="w-7 h-7 text-rose-300" />
                  Volunteer & Bereavement
                </h1>
                <p className="text-rose-200 mt-1">Volunteer Management & Family Support Services</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="text-rose-200 hover:bg-rose-700/50"
              onClick={fetchData}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-rose-900/50 border-b border-rose-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="grid grid-cols-5 gap-4">
            <div className="flex items-center gap-3 p-3 bg-blue-500/20 rounded-lg border border-blue-500/30">
              <Users className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-2xl font-bold text-blue-300">{volunteerSummary.total_volunteers || 0}</p>
                <p className="text-xs text-blue-400">Active Volunteers</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-green-500/20 rounded-lg border border-green-500/30">
              <Clock className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold text-green-300">{volunteerSummary.total_hours_this_month || 0}</p>
                <p className="text-xs text-green-400">Hours This Month</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-rose-500/20 rounded-lg border border-rose-500/30">
              <Heart className="w-8 h-8 text-rose-400" />
              <div>
                <p className="text-2xl font-bold text-rose-300">{bereavementSummary.active_cases || 0}</p>
                <p className="text-xs text-rose-400">Bereavement Cases</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
              <AlertTriangle className="w-8 h-8 text-yellow-400" />
              <div>
                <p className="text-2xl font-bold text-yellow-300">{dueFollowUps.length}</p>
                <p className="text-xs text-yellow-400">Follow-ups Due</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-purple-500/20 rounded-lg border border-purple-500/30">
              <Award className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-2xl font-bold text-purple-300">
                  {fivePercentReport?.percentage?.toFixed(1) || 0}%
                </p>
                <p className="text-xs text-purple-400">5% CMS Target</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-rose-900/50 border border-rose-500/30">
            <TabsTrigger value="volunteers" className="gap-2 data-[state=active]:bg-blue-600">
              <Users className="w-4 h-4" /> Volunteers
            </TabsTrigger>
            <TabsTrigger value="bereavement" className="gap-2 data-[state=active]:bg-rose-600">
              <Heart className="w-4 h-4" /> Bereavement
              {dueFollowUps.length > 0 && <Badge className="ml-1 bg-yellow-500">{dueFollowUps.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-2 data-[state=active]:bg-purple-600">
              <FileText className="w-4 h-4" /> 5% Report
            </TabsTrigger>
          </TabsList>

          {/* Volunteers Tab */}
          <TabsContent value="volunteers">
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2">
                <Card className="bg-rose-900/30 border-rose-500/30">
                  <CardHeader className="border-b border-rose-500/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-400" />
                          Volunteer Directory
                        </CardTitle>
                        <CardDescription className="text-rose-300">
                          Manage volunteers and track their activities
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => setVisitLogDialog(true)}
                        >
                          <ClipboardList className="w-4 h-4 mr-2" /> Log Visit
                        </Button>
                        <Button
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => setVolunteerDialog(true)}
                          data-testid="add-volunteer-btn"
                        >
                          <UserPlus className="w-4 h-4 mr-2" /> Add Volunteer
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {volunteers.length === 0 ? (
                      <div className="text-center py-12 text-rose-300">
                        <Users className="w-12 h-12 mx-auto mb-3 text-rose-500" />
                        <p>No volunteers registered</p>
                        <Button variant="link" onClick={() => setVolunteerDialog(true)} className="text-blue-400">
                          Add your first volunteer
                        </Button>
                      </div>
                    ) : (
                      <div className="divide-y divide-rose-500/20">
                        {volunteers.map(vol => (
                          <div key={vol.id} className="p-4 hover:bg-rose-800/30">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                                  <User className="w-6 h-6 text-blue-400" />
                                </div>
                                <div>
                                  <h4 className="font-medium text-white">{vol.name}</h4>
                                  <div className="flex items-center gap-4 mt-1 text-sm text-rose-300">
                                    <span className="flex items-center gap-1">
                                      <Mail className="w-3 h-3" /> {vol.email}
                                    </span>
                                    {vol.phone && (
                                      <span className="flex items-center gap-1">
                                        <Phone className="w-3 h-3" /> {vol.phone}
                                      </span>
                                    )}
                                  </div>
                                  {vol.skills && vol.skills.length > 0 && (
                                    <div className="flex gap-1 mt-2">
                                      {vol.skills.slice(0, 3).map((skill, idx) => (
                                        <Badge key={idx} className="bg-blue-500/20 text-blue-300 text-xs">
                                          {skill}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-green-400">{vol.total_hours || 0}</p>
                                <p className="text-xs text-rose-400">Total Hours</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div>
                <Card className="bg-rose-900/30 border-rose-500/30">
                  <CardHeader className="border-b border-rose-500/20">
                    <CardTitle className="text-white text-lg">Monthly Statistics</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    <div className="p-3 bg-blue-900/30 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-blue-300">Active Volunteers</span>
                        <span className="text-white font-bold">{volunteerSummary.active_volunteers || 0}</span>
                      </div>
                      <Progress value={volunteerSummary.active_volunteers ? 100 : 0} className="h-2" />
                    </div>
                    <div className="p-3 bg-green-900/30 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-green-300">Visits This Month</span>
                        <span className="text-white font-bold">{volunteerSummary.visits_this_month || 0}</span>
                      </div>
                    </div>
                    <div className="p-3 bg-purple-900/30 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-purple-300">Hours This Month</span>
                        <span className="text-white font-bold">{volunteerSummary.total_hours_this_month || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Bereavement Tab */}
          <TabsContent value="bereavement">
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2 space-y-6">
                {/* Due Follow-ups */}
                {dueFollowUps.length > 0 && (
                  <Card className="bg-yellow-900/30 border-yellow-500/30">
                    <CardHeader className="border-b border-yellow-500/20">
                      <CardTitle className="text-white flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-400" />
                        Follow-ups Due ({dueFollowUps.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="divide-y divide-yellow-500/20">
                        {dueFollowUps.map(followUp => (
                          <div key={followUp.id} className="p-4 hover:bg-yellow-900/20">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-white">{followUp.family_name}</h4>
                                <p className="text-sm text-yellow-300">
                                  Due: {new Date(followUp.due_date).toLocaleDateString()} • 
                                  Type: {followUp.follow_up_type}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => completeFollowUp(followUp.bereavement_id, followUp.id)}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" /> Complete
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Active Cases */}
                <Card className="bg-rose-900/30 border-rose-500/30">
                  <CardHeader className="border-b border-rose-500/20">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Heart className="w-5 h-5 text-rose-400" />
                      Active Bereavement Cases
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {bereavementCases.length === 0 ? (
                      <div className="text-center py-12 text-rose-300">
                        <Heart className="w-12 h-12 mx-auto mb-3 text-rose-500" />
                        <p>No active bereavement cases</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-rose-500/20">
                        {bereavementCases.map(bCase => {
                          const risk = BEREAVEMENT_RISK[bCase.risk_level] || BEREAVEMENT_RISK.standard;
                          return (
                            <div key={bCase.id} className="p-4 hover:bg-rose-800/30">
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-medium text-white">{bCase.family_name}</h4>
                                    <Badge className={`${risk.color}`}>{risk.label}</Badge>
                                  </div>
                                  <p className="text-sm text-rose-300">
                                    Patient: {bCase.patient_name} • 
                                    Started: {new Date(bCase.initiated_at).toLocaleDateString()}
                                  </p>
                                  <p className="text-sm text-rose-400 mt-1">
                                    Follow-ups: {bCase.completed_follow_ups || 0}/{bCase.total_follow_ups || 0} completed
                                  </p>
                                </div>
                                <Button
                                  size="sm"
                                  className="bg-purple-600 hover:bg-purple-700"
                                  onClick={() => {
                                    setSelectedItem(bCase);
                                    setAssessmentDialog(true);
                                  }}
                                >
                                  <ClipboardList className="w-4 h-4 mr-1" /> Assess
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div>
                <Card className="bg-rose-900/30 border-rose-500/30">
                  <CardHeader className="border-b border-rose-500/20">
                    <CardTitle className="text-white text-lg">Bereavement Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    <div className="p-3 bg-red-900/30 rounded-lg">
                      <p className="text-red-300 text-sm">High Risk Cases</p>
                      <p className="text-2xl font-bold text-red-400">{bereavementSummary.high_risk || 0}</p>
                    </div>
                    <div className="p-3 bg-yellow-900/30 rounded-lg">
                      <p className="text-yellow-300 text-sm">Moderate Risk Cases</p>
                      <p className="text-2xl font-bold text-yellow-400">{bereavementSummary.moderate_risk || 0}</p>
                    </div>
                    <div className="p-3 bg-green-900/30 rounded-lg">
                      <p className="text-green-300 text-sm">Standard Cases</p>
                      <p className="text-2xl font-bold text-green-400">{bereavementSummary.standard_risk || 0}</p>
                    </div>
                    <div className="p-3 bg-rose-800/30 rounded-lg">
                      <p className="text-rose-300 text-sm">Overdue Follow-ups</p>
                      <p className="text-2xl font-bold text-rose-400">{bereavementSummary.overdue_follow_ups || 0}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* 5% Report Tab */}
          <TabsContent value="reports">
            <Card className="bg-rose-900/30 border-rose-500/30">
              <CardHeader className="border-b border-rose-500/20">
                <CardTitle className="text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-400" />
                  CMS 5% Volunteer Cost Report
                </CardTitle>
                <CardDescription className="text-rose-300">
                  Medicare requires hospices to use volunteers to provide at least 5% of total patient care hours
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                {fivePercentReport ? (
                  <div className="grid grid-cols-3 gap-6">
                    <div className="col-span-2 space-y-6">
                      <div className="text-center p-8 bg-purple-900/30 rounded-xl border border-purple-500/30">
                        <p className="text-purple-300 text-sm mb-2">Current Volunteer Percentage</p>
                        <div className={`text-6xl font-bold ${fivePercentReport.meets_requirement ? "text-green-400" : "text-red-400"}`}>
                          {fivePercentReport.percentage?.toFixed(1) || 0}%
                        </div>
                        <Badge className={fivePercentReport.meets_requirement ? "bg-green-500 mt-3" : "bg-red-500 mt-3"}>
                          {fivePercentReport.meets_requirement ? "Meets CMS Requirement" : "Below Requirement"}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-rose-800/30 rounded-lg">
                          <p className="text-rose-300 text-sm">Total Patient Care Hours</p>
                          <p className="text-3xl font-bold text-white">{fivePercentReport.total_patient_hours || 0}</p>
                        </div>
                        <div className="p-4 bg-rose-800/30 rounded-lg">
                          <p className="text-rose-300 text-sm">Volunteer Hours</p>
                          <p className="text-3xl font-bold text-white">{fivePercentReport.volunteer_hours || 0}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="p-4 bg-purple-900/30 rounded-lg border border-purple-500/30">
                        <p className="text-purple-300 text-sm mb-2">Reporting Period</p>
                        <p className="text-white">{fivePercentReport.reporting_period || "Current Month"}</p>
                      </div>
                      <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-500/30">
                        <p className="text-blue-300 text-sm mb-2">Active Volunteers</p>
                        <p className="text-2xl font-bold text-white">{fivePercentReport.active_volunteers || 0}</p>
                      </div>
                      <div className="p-4 bg-green-900/30 rounded-lg border border-green-500/30">
                        <p className="text-green-300 text-sm mb-2">Hours Needed for 5%</p>
                        <p className="text-2xl font-bold text-white">
                          {fivePercentReport.meets_requirement ? "✓" : Math.ceil((fivePercentReport.total_patient_hours || 0) * 0.05 - (fivePercentReport.volunteer_hours || 0))}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-rose-300">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-rose-500" />
                    <p>Loading 5% report...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Volunteer Dialog */}
      <Dialog open={volunteerDialog} onOpenChange={setVolunteerDialog}>
        <DialogContent className="max-w-md bg-rose-900 border-rose-500/50">
          <DialogHeader>
            <DialogTitle className="text-white">Register New Volunteer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-rose-200">Name *</Label>
              <Input
                value={volunteerForm.name}
                onChange={(e) => setVolunteerForm({...volunteerForm, name: e.target.value})}
                className="bg-rose-800/50 border-rose-500/50 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-rose-200">Email *</Label>
              <Input
                type="email"
                value={volunteerForm.email}
                onChange={(e) => setVolunteerForm({...volunteerForm, email: e.target.value})}
                className="bg-rose-800/50 border-rose-500/50 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-rose-200">Phone</Label>
              <Input
                value={volunteerForm.phone}
                onChange={(e) => setVolunteerForm({...volunteerForm, phone: e.target.value})}
                className="bg-rose-800/50 border-rose-500/50 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-rose-200">Skills (comma-separated)</Label>
              <Input
                value={volunteerForm.skills}
                onChange={(e) => setVolunteerForm({...volunteerForm, skills: e.target.value})}
                placeholder="companionship, music, reading..."
                className="bg-rose-800/50 border-rose-500/50 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVolunteerDialog(false)} className="border-rose-500/50 text-rose-300">
              Cancel
            </Button>
            <Button onClick={registerVolunteer} className="bg-blue-600 hover:bg-blue-700">
              <UserPlus className="w-4 h-4 mr-2" /> Register
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visit Log Dialog */}
      <Dialog open={visitLogDialog} onOpenChange={setVisitLogDialog}>
        <DialogContent className="max-w-md bg-rose-900 border-rose-500/50">
          <DialogHeader>
            <DialogTitle className="text-white">Log Volunteer Visit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-rose-200">Volunteer</Label>
              <Select value={visitForm.volunteer_id} onValueChange={(v) => setVisitForm({...visitForm, volunteer_id: v})}>
                <SelectTrigger className="bg-rose-800/50 border-rose-500/50 text-white">
                  <SelectValue placeholder="Select volunteer..." />
                </SelectTrigger>
                <SelectContent>
                  {volunteers.map(vol => (
                    <SelectItem key={vol.id} value={vol.id}>{vol.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-rose-200">Hours</Label>
              <Input
                type="number"
                min="0.5"
                step="0.5"
                value={visitForm.hours}
                onChange={(e) => setVisitForm({...visitForm, hours: parseFloat(e.target.value) || 1})}
                className="bg-rose-800/50 border-rose-500/50 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-rose-200">Activities (comma-separated)</Label>
              <Input
                value={visitForm.activities}
                onChange={(e) => setVisitForm({...visitForm, activities: e.target.value})}
                placeholder="reading, companionship..."
                className="bg-rose-800/50 border-rose-500/50 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-rose-200">Notes</Label>
              <Textarea
                value={visitForm.notes}
                onChange={(e) => setVisitForm({...visitForm, notes: e.target.value})}
                rows={3}
                className="bg-rose-800/50 border-rose-500/50 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVisitLogDialog(false)} className="border-rose-500/50 text-rose-300">
              Cancel
            </Button>
            <Button onClick={logVisit} className="bg-green-600 hover:bg-green-700">
              <ClipboardList className="w-4 h-4 mr-2" /> Log Visit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bereavement Assessment Dialog */}
      <Dialog open={assessmentDialog} onOpenChange={setAssessmentDialog}>
        <DialogContent className="max-w-md bg-rose-900 border-rose-500/50">
          <DialogHeader>
            <DialogTitle className="text-white">Bereavement Assessment</DialogTitle>
            <DialogDescription className="text-rose-300">
              {selectedItem?.family_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-rose-200">Current Grief Stage</Label>
              <Select value={assessmentForm.grief_stage} onValueChange={(v) => setAssessmentForm({...assessmentForm, grief_stage: v})}>
                <SelectTrigger className="bg-rose-800/50 border-rose-500/50 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRIEF_STAGES.map(stage => (
                    <SelectItem key={stage} value={stage} className="capitalize">{stage}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-rose-200">Coping Score (1-10)</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={assessmentForm.coping_score}
                onChange={(e) => setAssessmentForm({...assessmentForm, coping_score: parseInt(e.target.value) || 5})}
                className="bg-rose-800/50 border-rose-500/50 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-rose-200">Support Level</Label>
              <Select value={assessmentForm.support_level} onValueChange={(v) => setAssessmentForm({...assessmentForm, support_level: v})}>
                <SelectTrigger className="bg-rose-800/50 border-rose-500/50 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excellent">Excellent</SelectItem>
                  <SelectItem value="adequate">Adequate</SelectItem>
                  <SelectItem value="limited">Limited</SelectItem>
                  <SelectItem value="isolated">Isolated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-rose-200">Assessment Notes</Label>
              <Textarea
                value={assessmentForm.notes}
                onChange={(e) => setAssessmentForm({...assessmentForm, notes: e.target.value})}
                rows={3}
                className="bg-rose-800/50 border-rose-500/50 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssessmentDialog(false)} className="border-rose-500/50 text-rose-300">
              Cancel
            </Button>
            <Button onClick={submitAssessment} className="bg-purple-600 hover:bg-purple-700">
              <CheckCircle className="w-4 h-4 mr-2" /> Submit Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
